import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { buildPaymentStyles, numOr0 } from './ui';
import { PaymentChoiceStep } from './PaymentChoiceStep';
import { PlanPickerStep } from './PlanPickerStep';
import { OtherPaymentStep } from './OtherPaymentStep';
import { membersApi } from '../../api/membersApi';
import { MemberSessionPickerStep } from './MemberSessionPickerStep';

const STEP = {
  CHOICE: 'CHOICE',
  PLAN: 'PLAN',
  SESSION: 'SESSION',
  DEBT_REDUCE: 'DEBT_REDUCE',
  OTHER: 'OTHER',
};

function resolveMemberId(member) {
  return member?.id ?? member?._id ?? member?.memberId ?? null;
}

function todayLocalDateStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

function moneyTRY(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '-';
  return `${new Intl.NumberFormat('tr-TR').format(n)} ₺`;
}

function normalizeStep(value) {
  const v = String(value || '')
    .toUpperCase()
    .trim();
  if (v === 'PLAN') return STEP.PLAN;
  if (v === 'SESSION') return STEP.SESSION;
  if (v === 'DEBT_REDUCE') return STEP.DEBT_REDUCE;
  if (v === 'OTHER') return STEP.OTHER;
  return STEP.CHOICE;
}

function ConfirmDialog({ open, title, detail, onClose, onConfirm, loading }) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <MDTypography fontWeight="bold">Onay</MDTypography>
      </DialogTitle>
      <DialogContent>
        <MDTypography fontWeight="bold">{title}</MDTypography>
        {detail ? (
          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
            {detail}
          </MDTypography>
        ) : null}
        <MDTypography sx={{ mt: 1.5 }}>İşlemi yapmak istediğine emin misin?</MDTypography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <MDButton variant="outlined" color="dark" onClick={onClose} disabled={loading}>
          Vazgeç
        </MDButton>
        <MDButton variant="contained" color="warning" onClick={onConfirm} disabled={loading}>
          {loading ? 'Kaydediliyor...' : 'Evet, Onayla'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export function PaymentReceiveModal({
  open,
  member,
  onClose,
  onSuccess,
  plans,
  sessions,
  currencyLabel = '₺',
  initialStep = 'CHOICE',
}) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const styles = useMemo(() => buildPaymentStyles(theme), [theme]);

  const [step, setStep] = useState(STEP.CHOICE);
  const [otherMethod, setOtherMethod] = useState('CASH');
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [otherDesc, setOtherDesc] = useState('');
  const [otherAmountTry, setOtherAmountTry] = useState('');
  const [debtReduceNote, setDebtReduceNote] = useState('');
  const [debtReduceAmountTry, setDebtReduceAmountTry] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const memberId = resolveMemberId(member);
  const fullName =
    member?.fullName || `${member?.name || ''} ${member?.surname || ''}`.trim() || 'Üye';

  useEffect(() => {
    if (!open) return;

    setStep(normalizeStep(initialStep));
    setSelectedPlanId(null);
    setOtherDesc('');
    setOtherAmountTry('');
    setDebtReduceNote('');
    setDebtReduceAmountTry('');
    setOtherMethod('CASH');
    setSaving(false);
    setError('');
    setDraft(null);
    setConfirmOpen(false);
  }, [open, initialStep]);

  const planOptions = useMemo(() => {
    const src = plans ?? member?.plans ?? member?.activePlans ?? [];
    return Array.isArray(src) ? src : [];
  }, [plans, member]);

  const sessionOptions = useMemo(() => {
    const src = sessions ?? member?.sessions ?? member?.activeSessions ?? [];
    const arr = Array.isArray(src) ? src : [];
    if (!memberId) return [];

    return arr.filter((s) => {
      const sid = s?.memberId ?? s?.member?.id ?? s?.member?.memberId ?? s?.participant?.memberId;
      return sid == null ? true : String(sid) === String(memberId);
    });
  }, [sessions, member, memberId]);

  const goBackToChoice = () => {
    setError('');
    setDraft(null);
    setStep(STEP.CHOICE);
  };

  useEffect(() => {
    if (step !== STEP.OTHER) return;
    const amountTry = numOr0(otherAmountTry);
    const note = (otherDesc || '').trim();

    if (!memberId || amountTry <= 0 || !note) {
      setDraft(null);
      return;
    }

    setDraft({
      kind: 'OTHER',
      amountTry,
      title: `${fullName} • Diğer Ödeme`,
      detail: `${moneyTRY(amountTry)} • ${note}`,
      action: async () => {
        await membersApi.createPayment({
          memberId,
          memberPlanId: null,
          amountTry,
          method: otherMethod || 'CASH',
          purpose: 'GENERAL',
          paidDate: todayLocalDateStr(),
          note,
        });
      },
    });
  }, [step, memberId, fullName, otherAmountTry, otherDesc, otherMethod]);

  useEffect(() => {
    if (step !== STEP.DEBT_REDUCE) return;
    const amountTry = numOr0(debtReduceAmountTry);
    const note = (debtReduceNote || '').trim();

    if (!memberId || amountTry <= 0 || !note) {
      setDraft(null);
      return;
    }

    setDraft({
      kind: 'DEBT_REDUCE',
      amountTry,
      title: `${fullName} • Borç Azalt`,
      detail: `${moneyTRY(amountTry)} • ${note}`,
      action: async () => {
        await membersApi.reduceDebt(memberId, {
          amountTry,
          requestId:
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
              ? crypto.randomUUID()
              : `${memberId}-${Date.now()}`,
          note,
        });
      },
    });
  }, [step, memberId, fullName, debtReduceAmountTry, debtReduceNote]);

  const handlePlanDraft = (d) => {
    if (!d || !memberId) {
      setDraft(null);
      return;
    }

    if (d.kind === 'PLAN_DEBT') {
      const amountTry = d.amountTry;
      setDraft({
        kind: 'PLAN_DEBT',
        amountTry,
        title: `${fullName} • Paket Borç Tahsilatı`,
        detail: `${d.planName} • ${moneyTRY(amountTry)} • (${d.startAtLabel} - ${d.endAtLabel})`,
        action: async () => {
          await membersApi.createPayment({
            memberId,
            memberPlanId: d.memberPlanId,
            amountTry,
            method: d.paymentMethod || 'CASH',
            purpose: 'PLAN',
            paidDate: d.paidDate || todayLocalDateStr(),
            note: d.note || 'Borç tahsilatı',
          });
        },
      });
      return;
    }

    if (d.kind === 'PLAN_EARLY') {
      const amountTry = d.amountTry;
      setDraft({
        kind: 'PLAN_EARLY',
        amountTry,
        title: `${fullName} • Paket Erken Ödeme`,
        detail: `${d.planName} • ${moneyTRY(amountTry)} • ${
          d.renewPackage ? 'Paket yenilenecek' : 'Yenileme yok'
        }`,
        action: async () => {
          await membersApi.earlyTransfer(memberId, {
            memberId,
            fromPlanId: d.fromPlanId,
            renewPackage: !!d.renewPackage,
            amountTry,
            method: d.paymentMethod || 'CASH',
            paidDate: d.paidDate || todayLocalDateStr(),
            note: d.note || 'Erken ödeme',
          });
        },
      });
      return;
    }

    setDraft(null);
  };

  const titleByStep =
    step === STEP.PLAN
      ? 'Paket Ödemesi'
      : step === STEP.SESSION
      ? 'Seans Ödemesi'
      : step === STEP.DEBT_REDUCE
      ? 'Borç Azalt'
      : step === STEP.OTHER
      ? 'Diğer Ödeme'
      : 'Ödeme Al';

  const subtitleByStep =
    step === STEP.PLAN
      ? 'Paket seç, tutarı gir, onayla.'
      : step === STEP.SESSION
      ? 'Seans seç, tutarı gir, onayla.'
      : step === STEP.DEBT_REDUCE
      ? 'Borçtan düşecek tutarı ve kısa notu gir.'
      : step === STEP.OTHER
      ? 'Açıklama ve tutar gir, onayla.'
      : `${fullName} için ödeme türünü seç.`;

  const canConfirm = !!draft && !saving;

  const confirmAndPay = async () => {
    if (!draft || saving) return;
    try {
      setSaving(true);
      setError('');
      await draft.action();
      setConfirmOpen(false);
      onSuccess?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'İşlem kaydedilemedi');
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const lockedStep = initialStep !== 'CHOICE';

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '18px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={styles.headerSx}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1.25}>
              <MDBox sx={styles.iconBadgeSx}>
                <Icon sx={{ fontSize: 20, color: alpha(styles.orange, 0.95) }}>payments</Icon>
              </MDBox>
              <MDBox>
                <MDTypography fontWeight="bold">{titleByStep}</MDTypography>
                <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                  {subtitleByStep}
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox display="flex" gap={1} alignItems="center">
              {step !== STEP.CHOICE && !lockedStep ? (
                <MDButton
                  variant="outlined"
                  color="warning"
                  onClick={goBackToChoice}
                  sx={{ borderRadius: '12px' }}
                >
                  Geri
                </MDButton>
              ) : null}
              <MDButton
                variant="text"
                color="dark"
                onClick={onClose}
                sx={{ borderRadius: '12px' }}
                disabled={saving}
              >
                Kapat
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: step === STEP.CHOICE ? 2 : 1.5 }}>
          {step === STEP.CHOICE ? (
            <PaymentChoiceStep
              styles={styles}
              planCount={planOptions.length}
              sessionCount={sessionOptions.length}
              onGoPlan={() => setStep(STEP.PLAN)}
              onGoSession={() => setStep(STEP.SESSION)}
              onGoDebtReduce={() => setStep(STEP.DEBT_REDUCE)}
              onGoOther={() => setStep(STEP.OTHER)}
              onCancel={onClose}
            />
          ) : null}

          {step === STEP.PLAN ? (
            <PlanPickerStep
              theme={theme}
              styles={styles}
              memberId={memberId}
              selectedPlanId={selectedPlanId}
              onSelectPlanId={setSelectedPlanId}
              onDraftChange={handlePlanDraft}
            />
          ) : null}

          {step === STEP.SESSION ? (
            <MemberSessionPickerStep memberId={memberId} onDraftChange={(d) => setDraft(d)} />
          ) : null}

          {step === STEP.OTHER ? (
            <OtherPaymentStep
              theme={theme}
              styles={styles}
              currencyLabel={currencyLabel}
              otherDesc={otherDesc}
              setOtherDesc={setOtherDesc}
              otherAmountTry={otherAmountTry}
              setOtherAmountTry={setOtherAmountTry}
              otherMethod={otherMethod}
              setOtherMethod={setOtherMethod}
              error={error}
            />
          ) : null}

          {step === STEP.DEBT_REDUCE ? (
            <OtherPaymentStep
              theme={theme}
              styles={styles}
              currencyLabel={currencyLabel}
              title="Borç Azaltma Notu"
              subtitle="Örn: Elden alındı / dışarıda tahsil edildi / manuel düzeltme"
              amountTitle="Azaltılacak Tutar"
              amountSubtitle={`Borçtan düşülecek tutarı gir (${currencyLabel})`}
              previewLabel="Borçtan düşecek"
              hideMethod
              otherDesc={debtReduceNote}
              setOtherDesc={setDebtReduceNote}
              otherAmountTry={debtReduceAmountTry}
              setOtherAmountTry={setDebtReduceAmountTry}
              error={error}
            />
          ) : null}

          {!!error ? (
            <MDTypography variant="caption" sx={{ color: 'error.main', mt: 1, display: 'block' }}>
              {error}
            </MDTypography>
          ) : null}
        </DialogContent>

        {step !== STEP.CHOICE ? (
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <MDButton
              variant="outlined"
              color="dark"
              onClick={goBackToChoice}
              sx={{ borderRadius: '12px' }}
              disabled={saving}
            >
              Geri
            </MDButton>
            <MDBox sx={{ flex: 1 }} />
            <MDButton
              variant="contained"
              color="warning"
              onClick={() => setConfirmOpen(true)}
              disabled={!canConfirm}
              sx={{ borderRadius: '12px' }}
            >
              {step === STEP.DEBT_REDUCE ? 'Borcu Azalt' : 'Ödemeyi Al'}
            </MDButton>
          </DialogActions>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        title={draft?.title || 'İşlem'}
        detail={draft?.detail || ''}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmAndPay}
        loading={saving}
      />
    </>
  );
}
